import { usePostHog } from '@posthog/react';
import { type FC, useEffect, useRef } from 'react';
import { useAuth } from '@/app/hooks/auth-provider.tsx';
import {
  identifyAnalyticsUser,
  registerAnalyticsSuperProperties,
  subscriptionChanged,
  subscriptionSnapshot,
} from './person-properties.ts';

export const PostHogIdentity: FC = () => {
  const posthog = usePostHog();
  const { user, isImpersonating, isLoading } = useAuth();
  const lastIdentifiedKey = useRef<string | null>(null);
  const lastSubscriptionSnapshot = useRef<ReturnType<typeof subscriptionSnapshot>>(null);

  useEffect(() => {
    if (!posthog || isLoading) {
      return;
    }

    if (!user) {
      lastIdentifiedKey.current = null;
      lastSubscriptionSnapshot.current = null;
      registerAnalyticsSuperProperties(null, false);
      return;
    }

    registerAnalyticsSuperProperties(user, isImpersonating);

    if (isImpersonating) {
      lastIdentifiedKey.current = null;
      return;
    }

    const shouldIdentify =
      lastIdentifiedKey.current !== user.key ||
      subscriptionChanged(lastSubscriptionSnapshot.current, user);

    if (!shouldIdentify) {
      return;
    }

    identifyAnalyticsUser(user, isImpersonating);
    lastIdentifiedKey.current = user.key;
    lastSubscriptionSnapshot.current = subscriptionSnapshot(user);
  }, [posthog, user, isImpersonating, isLoading]);

  return null;
};
